/**
 * AutoCAD-style SVG icons for ribbon toolbar buttons.
 * Each icon is a 20x20 SVG matching the visual language of AutoCAD ribbon.
 * Color: #4FC3F7 (cyan/light-blue) for geometry, #FFA726 for tools.
 */

const S = 20; // icon size
const c = "#4FC3F7"; // cyan — geometry color
const o = "#FFA726"; // orange — tool color
const g = "#66BB6A"; // green — annotation
const w = "#E0E0E0"; // white/light — text

type P = { className?: string };

// ── Draw ──

export function IconLine(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="3" y1="17" x2="17" y2="3" stroke={c} strokeWidth="1.8" />
      <rect x="1.5" y="15.5" width="3" height="3" fill={c} />
      <rect x="15.5" y="1.5" width="3" height="3" fill={c} />
    </svg>
  );
}

export function IconCircle(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="8" stroke={c} strokeWidth="1.6" />
      <circle cx="10" cy="10" r="1.2" fill={c} />
    </svg>
  );
}

export function IconArc(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <path d="M4 16 A10 10 0 0 1 16 4" stroke={c} strokeWidth="1.8" fill="none" />
      <rect x="2.5" y="14.5" width="3" height="3" fill={c} />
      <rect x="14.5" y="2.5" width="3" height="3" fill={c} />
    </svg>
  );
}

export function IconPolyline(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <polyline points="2,16 7,5 13,14 18,3" stroke={c} strokeWidth="1.8" fill="none" strokeLinejoin="round" />
      <rect x="0.5" y="14.5" width="3" height="3" fill={c} />
      <rect x="5.5" y="3.5" width="3" height="3" fill={c} />
      <rect x="11.5" y="12.5" width="3" height="3" fill={c} />
      <rect x="16.5" y="1.5" width="3" height="3" fill={c} />
    </svg>
  );
}

export function IconRectangle(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="3" y="5" width="14" height="10" stroke={c} strokeWidth="1.6" rx="0.5" />
    </svg>
  );
}

export function IconBlock(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="2" y="6" width="10" height="10" stroke={c} strokeWidth="1.4" />
      <rect x="8" y="2" width="10" height="10" stroke={c} strokeWidth="1.4" strokeDasharray="2 1.5" />
    </svg>
  );
}

export function IconArray(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="2" y="2" width="6" height="6" stroke={c} strokeWidth="1.2" />
      <rect x="12" y="2" width="6" height="6" stroke={c} strokeWidth="1.2" />
      <rect x="2" y="12" width="6" height="6" stroke={c} strokeWidth="1.2" />
      <rect x="12" y="12" width="6" height="6" stroke={c} strokeWidth="1.2" />
    </svg>
  );
}

// ── Annotate ──

export function IconText(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <text x="5" y="16" fill={w} fontSize="16" fontWeight="700" fontFamily="sans-serif">A</text>
    </svg>
  );
}

export function IconMText(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <text x="1" y="14" fill={w} fontSize="13" fontWeight="700" fontFamily="sans-serif">A</text>
      <line x1="12" y1="5" x2="19" y2="5" stroke={w} strokeWidth="1.2" />
      <line x1="12" y1="9" x2="19" y2="9" stroke={w} strokeWidth="1.2" />
      <line x1="12" y1="13" x2="17" y2="13" stroke={w} strokeWidth="1.2" />
    </svg>
  );
}

export function IconDimension(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="3" y1="10" x2="17" y2="10" stroke={c} strokeWidth="1.4" />
      <polygon points="3,10 6,8 6,12" fill={c} />
      <polygon points="17,10 14,8 14,12" fill={c} />
      <line x1="3" y1="6" x2="3" y2="14" stroke={c} strokeWidth="1" />
      <line x1="17" y1="6" x2="17" y2="14" stroke={c} strokeWidth="1" />
    </svg>
  );
}

export function IconDimAligned(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="3" y1="16" x2="17" y2="4" stroke={c} strokeWidth="1.4" />
      <polygon points="3,16 5.5,14 6,16.5" fill={c} />
      <polygon points="17,4 14.5,6 14,3.5" fill={c} />
      <line x1="1" y1="14" x2="5" y2="18" stroke={c} strokeWidth="0.8" />
      <line x1="15" y1="2" x2="19" y2="6" stroke={c} strokeWidth="0.8" />
    </svg>
  );
}

export function IconDimAngular(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="4" y1="16" x2="16" y2="16" stroke={c} strokeWidth="1.2" />
      <line x1="4" y1="16" x2="14" y2="4" stroke={c} strokeWidth="1.2" />
      <path d="M12 16 A8 8 0 0 1 11 8" stroke={c} strokeWidth="1.4" fill="none" />
      <polygon points="11,8 9.5,10.5 12,10" fill={c} />
    </svg>
  );
}

export function IconDim3Point(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="3" y1="17" x2="10" y2="3" stroke={c} strokeWidth="1.2" />
      <line x1="10" y1="3" x2="17" y2="17" stroke={c} strokeWidth="1.2" />
      <path d="M6 12 A6 6 0 0 1 14 12" stroke={c} strokeWidth="1.4" fill="none" />
      <circle cx="3" cy="17" r="1.5" fill={c} />
      <circle cx="10" cy="3" r="1.5" fill={c} />
      <circle cx="17" cy="17" r="1.5" fill={c} />
    </svg>
  );
}

export function IconDimRadius(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <path d="M4 16 A10 10 0 0 1 16 4" stroke={c} strokeWidth="1.4" fill="none" />
      <line x1="10" y1="10" x2="16" y2="4" stroke={c} strokeWidth="1.4" />
      <polygon points="16,4 13,5.5 14.5,7" fill={c} />
      <text x="7" y="9" fill={c} fontSize="7" fontWeight="700">R</text>
    </svg>
  );
}

export function IconDimDiameter(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="7.5" stroke={c} strokeWidth="1.4" />
      <line x1="3" y1="10" x2="17" y2="10" stroke={c} strokeWidth="1.4" />
      <polygon points="3,10 5.5,8.5 5.5,11.5" fill={c} />
      <polygon points="17,10 14.5,8.5 14.5,11.5" fill={c} />
    </svg>
  );
}

export function IconDimOrdinate(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="5" y1="15" x2="5" y2="5" stroke={c} strokeWidth="1.2" />
      <line x1="5" y1="15" x2="16" y2="15" stroke={c} strokeWidth="1.2" />
      <line x1="5" y1="5" x2="2" y2="2" stroke={c} strokeWidth="1" />
      <line x1="16" y1="15" x2="18" y2="18" stroke={c} strokeWidth="1" />
      <circle cx="5" cy="15" r="1.5" fill={c} />
    </svg>
  );
}

export function IconMLeader(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <polyline points="3,15 8,10 12,10" stroke={c} strokeWidth="1.4" fill="none" />
      <polygon points="3,15 5,13.5 5.5,15.5" fill={c} />
      <rect x="12" y="7" width="7" height="6" stroke={c} strokeWidth="1" rx="1" />
      <text x="13.5" y="12" fill={c} fontSize="5" fontWeight="600">Abc</text>
    </svg>
  );
}

export function IconMLeaderAdd(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <polyline points="3,15 8,10 12,10" stroke={c} strokeWidth="1.2" fill="none" />
      <polygon points="3,15 5,13.5 5.5,15.5" fill={c} />
      <circle cx="15" cy="6" r="4" stroke={g} strokeWidth="1.4" fill="none" />
      <line x1="15" y1="3.5" x2="15" y2="8.5" stroke={g} strokeWidth="1.4" />
      <line x1="12.5" y1="6" x2="17.5" y2="6" stroke={g} strokeWidth="1.4" />
    </svg>
  );
}

export function IconMLeaderRemove(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <polyline points="3,15 8,10 12,10" stroke={c} strokeWidth="1.2" fill="none" />
      <polygon points="3,15 5,13.5 5.5,15.5" fill={c} />
      <circle cx="15" cy="6" r="4" stroke="#EF5350" strokeWidth="1.4" fill="none" />
      <line x1="12.5" y1="6" x2="17.5" y2="6" stroke="#EF5350" strokeWidth="1.4" />
    </svg>
  );
}

export function IconRevCloud(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <path d="M4,14 a3,3 0 0,1 4,-2 a3,3 0 0,1 4,0 a3,3 0 0,1 4,2 a3,3 0 0,1 -2,3 a3,3 0 0,1 -4,0 a3,3 0 0,1 -4,-1 a3,3 0 0,1 -2,-2z" stroke={c} strokeWidth="1.2" fill="none" />
    </svg>
  );
}

export function IconHatch(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="3" y="3" width="14" height="14" stroke={c} strokeWidth="1.2" />
      <line x1="3" y1="8" x2="8" y2="3" stroke={c} strokeWidth="0.8" />
      <line x1="3" y1="13" x2="13" y2="3" stroke={c} strokeWidth="0.8" />
      <line x1="3" y1="17" x2="17" y2="3" stroke={c} strokeWidth="0.8" />
      <line x1="8" y1="17" x2="17" y2="8" stroke={c} strokeWidth="0.8" />
      <line x1="13" y1="17" x2="17" y2="13" stroke={c} strokeWidth="0.8" />
    </svg>
  );
}

// ── Modify ──

export function IconMove(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="10" y1="3" x2="10" y2="17" stroke={c} strokeWidth="1.2" />
      <line x1="3" y1="10" x2="17" y2="10" stroke={c} strokeWidth="1.2" />
      <polygon points="10,2 8,5 12,5" fill={c} />
      <polygon points="10,18 8,15 12,15" fill={c} />
      <polygon points="2,10 5,8 5,12" fill={c} />
      <polygon points="18,10 15,8 15,12" fill={c} />
    </svg>
  );
}

export function IconCopy(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="2" y="6" width="10" height="10" stroke={c} strokeWidth="1.2" />
      <rect x="8" y="2" width="10" height="10" stroke={c} strokeWidth="1.2" fill="rgba(79,195,247,0.1)" />
    </svg>
  );
}

export function IconMirror(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <polygon points="3,5 3,15 8,10" stroke={c} strokeWidth="1.2" fill="none" />
      <polygon points="17,5 17,15 12,10" stroke={c} strokeWidth="1.2" fill="none" strokeDasharray="2 1" />
      <line x1="10" y1="2" x2="10" y2="18" stroke={c} strokeWidth="1" strokeDasharray="3 2" />
    </svg>
  );
}

export function IconRotate(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <path d="M15 5 A7 7 0 1 1 5 5" stroke={c} strokeWidth="1.6" fill="none" />
      <polygon points="15,5 13,2 17,2" fill={c} />
    </svg>
  );
}

export function IconScale(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="2" y="10" width="7" height="7" stroke={c} strokeWidth="1.2" />
      <rect x="6" y="3" width="12" height="12" stroke={c} strokeWidth="1.2" strokeDasharray="2 1.5" />
      <line x1="4" y1="16" x2="16" y2="4" stroke={c} strokeWidth="0.8" />
      <polygon points="16,4 13,5 15,7" fill={c} />
    </svg>
  );
}

export function IconOffset(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <path d="M6,3 L6,17" stroke={c} strokeWidth="1.6" />
      <path d="M14,3 L14,17" stroke={c} strokeWidth="1.6" strokeDasharray="2 1.5" />
      <line x1="7" y1="10" x2="13" y2="10" stroke={c} strokeWidth="0.8" />
      <polygon points="13,10 11,8.5 11,11.5" fill={c} />
    </svg>
  );
}

export function IconStretch(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="2" y="5" width="10" height="10" stroke={c} strokeWidth="1.2" />
      <line x1="12" y1="8" x2="18" y2="4" stroke={c} strokeWidth="1.2" />
      <polygon points="18,4 15,5 16.5,7" fill={c} />
      <rect x="10.5" y="13.5" width="3" height="3" fill={c} />
    </svg>
  );
}

export function IconTrim(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="4" y1="4" x2="16" y2="16" stroke={o} strokeWidth="1.8" />
      <line x1="4" y1="16" x2="16" y2="4" stroke={o} strokeWidth="1.8" />
      <circle cx="10" cy="10" r="2" fill={o} />
    </svg>
  );
}

export function IconExtend(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="3" y1="10" x2="12" y2="10" stroke={c} strokeWidth="1.6" />
      <line x1="17" y1="3" x2="17" y2="17" stroke={c} strokeWidth="1.6" />
      <polygon points="12,10 10,8 10,12" fill={c} />
      <line x1="12" y1="10" x2="17" y2="10" stroke={c} strokeWidth="1" strokeDasharray="2 1.5" />
    </svg>
  );
}

export function IconFillet(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="3" y1="17" x2="3" y2="10" stroke={c} strokeWidth="1.6" />
      <line x1="10" y1="17" x2="17" y2="17" stroke={c} strokeWidth="1.6" />
      <path d="M3,10 Q3,17 10,17" stroke={o} strokeWidth="1.8" fill="none" />
    </svg>
  );
}

export function IconChamfer(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="3" y1="17" x2="3" y2="8" stroke={c} strokeWidth="1.6" />
      <line x1="12" y1="17" x2="17" y2="17" stroke={c} strokeWidth="1.6" />
      <line x1="3" y1="8" x2="12" y2="17" stroke={o} strokeWidth="1.8" />
    </svg>
  );
}

export function IconBreak(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="2" y1="10" x2="7" y2="10" stroke={c} strokeWidth="1.6" />
      <line x1="13" y1="10" x2="18" y2="10" stroke={c} strokeWidth="1.6" />
      <path d="M7,10 L8,6 L9,14 L10,6 L11,14 L12,6 L13,10" stroke={o} strokeWidth="1" fill="none" />
    </svg>
  );
}

export function IconJoin(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="2" y1="10" x2="8" y2="10" stroke={c} strokeWidth="1.6" />
      <line x1="12" y1="10" x2="18" y2="10" stroke={c} strokeWidth="1.6" />
      <polygon points="8,10 6,8 6,12" fill={g} />
      <polygon points="12,10 14,8 14,12" fill={g} />
    </svg>
  );
}

export function IconExplode(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="8" y="8" width="4" height="4" stroke={c} strokeWidth="1" />
      <line x1="8" y1="8" x2="3" y2="3" stroke={o} strokeWidth="1.2" />
      <line x1="12" y1="8" x2="17" y2="3" stroke={o} strokeWidth="1.2" />
      <line x1="8" y1="12" x2="3" y2="17" stroke={o} strokeWidth="1.2" />
      <line x1="12" y1="12" x2="17" y2="17" stroke={o} strokeWidth="1.2" />
      <polygon points="3,3 5,3.5 3.5,5" fill={o} />
      <polygon points="17,3 15,3.5 16.5,5" fill={o} />
      <polygon points="3,17 5,16.5 3.5,15" fill={o} />
      <polygon points="17,17 15,16.5 16.5,15" fill={o} />
    </svg>
  );
}

export function IconLengthen(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="3" y1="10" x2="14" y2="10" stroke={c} strokeWidth="1.6" />
      <polygon points="14,10 12,8 12,12" fill={c} />
      <line x1="14" y1="10" x2="18" y2="10" stroke={c} strokeWidth="1.2" strokeDasharray="2 1" />
      <line x1="3" y1="7" x2="3" y2="13" stroke={c} strokeWidth="1" />
    </svg>
  );
}

/** @public */
export function IconErase(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <line x1="4" y1="4" x2="16" y2="16" stroke="#EF5350" strokeWidth="2" />
      <line x1="16" y1="4" x2="4" y2="16" stroke="#EF5350" strokeWidth="2" />
    </svg>
  );
}

// ── Properties / Misc ──

export function IconProperties(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="3" y="2" width="14" height="16" stroke={c} strokeWidth="1.2" rx="1" />
      <line x1="6" y1="6" x2="14" y2="6" stroke={c} strokeWidth="1" />
      <line x1="6" y1="9" x2="14" y2="9" stroke={c} strokeWidth="1" />
      <line x1="6" y1="12" x2="14" y2="12" stroke={c} strokeWidth="1" />
      <line x1="6" y1="15" x2="11" y2="15" stroke={c} strokeWidth="1" />
    </svg>
  );
}

export function IconMatchProp(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="12" y="2" width="6" height="10" rx="1" stroke={o} strokeWidth="1.2" />
      <line x1="15" y1="12" x2="6" y2="17" stroke={o} strokeWidth="1.6" />
      <rect x="3" y="15" width="6" height="4" fill={o} rx="1" />
    </svg>
  );
}

export function IconChProp(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="3" y="2" width="14" height="16" stroke={c} strokeWidth="1.2" rx="1" />
      <line x1="6" y1="6" x2="14" y2="6" stroke={c} strokeWidth="1" />
      <line x1="6" y1="9" x2="14" y2="9" stroke={c} strokeWidth="1" />
      <path d="M13,11 L17,7 L18,8 L14,12 Z" fill={o} />
    </svg>
  );
}

export function IconClearSelection(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="3" y="3" width="14" height="14" stroke={c} strokeWidth="1" strokeDasharray="3 2" />
      <line x1="6" y1="6" x2="14" y2="14" stroke="#EF5350" strokeWidth="1.8" />
      <line x1="14" y1="6" x2="6" y2="14" stroke="#EF5350" strokeWidth="1.8" />
    </svg>
  );
}

export function IconHistory(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="8" stroke={c} strokeWidth="1.2" />
      <line x1="10" y1="5" x2="10" y2="10" stroke={c} strokeWidth="1.4" />
      <line x1="10" y1="10" x2="14" y2="13" stroke={c} strokeWidth="1.4" />
    </svg>
  );
}

export function IconOverview(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="8" r="5" stroke={c} strokeWidth="1.2" />
      <line x1="14" y1="12" x2="18" y2="16" stroke={c} strokeWidth="1.6" />
    </svg>
  );
}

export function IconAudit(_: P) {
  return (
    <svg width={S} height={S} viewBox="0 0 20 20" fill="none">
      <rect x="3" y="2" width="14" height="16" stroke={c} strokeWidth="1.2" rx="1" />
      <polyline points="6,10 8,12 13,7" stroke={g} strokeWidth="1.8" fill="none" />
    </svg>
  );
}

// ── Icon map by action ID ──

export const ribbonIconMap: Record<string, () => JSX.Element> = {
  line: IconLine,
  circle: IconCircle,
  arc: IconArc,
  polyline: IconPolyline,
  rectangle: IconRectangle,
  block: IconBlock,
  array: IconArray,
  text: IconText,
  mtext: IconMText,
  dimension: IconDimension,
  dimaligned: IconDimAligned,
  dimangular: IconDimAngular,
  dim3point: IconDim3Point,
  dimradius: IconDimRadius,
  dimdiameter: IconDimDiameter,
  dimordinate: IconDimOrdinate,
  mleader: IconMLeader,
  "mleader-add-leader": IconMLeaderAdd,
  "mleader-remove-leader": IconMLeaderRemove,
  revcloud: IconRevCloud,
  hatch: IconHatch,
  history: IconHistory,
  ai: IconOverview,
  move: IconMove,
  copy: IconCopy,
  mirror: IconMirror,
  offset: IconOffset,
  rotate: IconRotate,
  scale: IconScale,
  stretch: IconStretch,
  trim: IconTrim,
  extend: IconExtend,
  fillet: IconFillet,
  chamfer: IconChamfer,
  break: IconBreak,
  join: IconJoin,
  explode: IconExplode,
  lengthen: IconLengthen,
  properties: IconProperties,
  chprop: IconChProp,
  matchprop: IconMatchProp,
  "clear-selection": IconClearSelection,
  overview: IconOverview,
  audit: IconAudit,
};
